// src/pages/project/ProjectCreatePage.tsx
// Composer-style create surface (docs/ux/14-forms.md): ghost title + description,
// compact property row, progressive disclosure for the rarely-used date fields.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { Button, ErrorAlert } from '@react/components';
import { resourceStore } from '@core/stores/resource/ResourceStore';
import {
  validateProjectForm,
  methodologyOptions,
  statusOptions,
} from '@/features/project/logic';
import { Select } from '@/components/Select';
import { TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';

function toProjectKey(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 6);
}

export const ProjectCreatePage = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const store = useProject();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showMore, setShowMore] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    methodology: 'PROJECT_METHODOLOGY_KANBAN',
    status: 'PROJECT_STATUS_PLANNING',
    start_date: '',
    target_end_date: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleNameChange = (value: string) => {
    handleChange('name', value);
    // Auto-generate key from name if key is empty or was auto-generated
    if (!formData.key || formData.key === toProjectKey(formData.name)) {
      handleChange('key', toProjectKey(value));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateProjectForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: formData.name.trim(),
        key: formData.key.trim(),
        description: formData.description.trim(),
        methodology: formData.methodology,
        status: formData.status,
      };
      if (formData.start_date) payload.start_date = new Date(formData.start_date).toISOString();
      if (formData.target_end_date) payload.target_end_date = new Date(formData.target_end_date).toISOString();

      // Arriving from a team: the project belongs to it via `parent`.
      const teamIdParam = searchParams.get('team_id');
      if (teamIdParam) {
        payload.parent = { id: teamIdParam, type_id: 'team', type_name: 'team' };
      }

      const created = await store.CreateProject(payload as never);

      // The create contract has no status field — the service always creates
      // Planning. Honor a different choice with a follow-up update.
      if (created?.entity?.id && formData.status && formData.status !== 'PROJECT_STATUS_PLANNING') {
        try {
          await store.UpdateProject(created.entity.id, {
            project: { ...created, status: formData.status },
            field_mask: ['status'],
          } as never);
        } catch { /* project exists; status stays Planning */ }
      }

      // Also link team → project in resource links so the project shows in
      // the team's right-rail resources; `parent` above is the primary
      // ownership reference. Then return to the team's Projects tab.
      const teamId = teamIdParam;
      const projectEntity = created?.entity;
      if (teamId && projectEntity?.id) {
        try {
          await resourceStore.LinkResources({
            source: {
              id: teamId,
              type_id: 'team',
              type_name: 'team',
              platform_id: projectEntity.platform_id,
              tenant_id: projectEntity.tenant_id,
              product_id: projectEntity.product_id,
            },
            target: projectEntity,
            relation_type: 'RELATION_TYPE_CHILD',
          } as never);
        } catch { /* link is best-effort; the project itself is created */ }
        navigate(`/teams/${teamId}?tab=projects`);
        return;
      }
      navigate('/projects');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = !!formData.name.trim() && !isSubmitting;

  // ⌘⏎ submits from anywhere on the composer, matching the create-dialog convention.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSubmit) {
        e.preventDefault();
        handleSubmit();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSubmit, formData]);

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <nav className="text-sm">
        <Link to="/projects" className="text-muted-foreground hover:text-foreground">
          {t('project.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('project.create')}</span>
      </nav>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="rounded-2xl border border-border bg-popover shadow-sm">
        <div className="px-4 pb-1 pt-4">
          <TitleInput
            value={formData.name}
            onChange={handleNameChange}
            placeholder={t('project.field.namePlaceholder')}
            initialFocus
            aria-label={t('project.field.name')}
          />
          {errors.name && (
            <p className="mt-1 text-xs text-destructive">
              {t(errors.name, { field: t('project.field.name'), min: 2, max: 100 })}
            </p>
          )}
          <AutoGrowTextarea
            value={formData.description}
            onChange={(v) => handleChange('description', v)}
            placeholder={t('project.field.descriptionPlaceholder')}
            className="mt-1"
          />
        </div>

        <div className="space-y-3 px-4 pb-4 pt-2">
          <FormRow label={t('project.field.key')} labelWidth="sm" htmlFor="project-key" hint={t('project.field.keyHint')}>
            <input
              id="project-key"
              type="text"
              value={formData.key}
              onChange={(e) => handleChange('key', e.target.value.toUpperCase())}
              placeholder={t('project.field.keyPlaceholder')}
              autoCapitalize="off"
              className="w-32 rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.key && <p className="mt-1 text-xs text-destructive">{t(errors.key)}</p>}
          </FormRow>

          <FormRow label={t('project.field.methodology')} labelWidth="sm">
            <Select
              value={formData.methodology}
              onChange={(e) => handleChange('methodology', e.target.value)}
              options={methodologyOptions.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
              fullWidth={false}
              className="w-52"
              aria-label={t('project.field.methodology')}
            />
          </FormRow>

          <FormRow label={t('project.field.status')} labelWidth="sm">
            <SegmentedControl
              aria-label={t('project.field.status')}
              size="sm"
              value={formData.status}
              onChange={(v) => handleChange('status', v)}
              options={statusOptions.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
            />
          </FormRow>

          {!showMore ? (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="text-xs font-medium text-primary hover:text-primary/80"
            >
              + {t('task.moreFields')}
            </button>
          ) : (
            <FormRow label={t('project.field.timeline')} labelWidth="sm">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {t('project.field.startDate')}
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {t('project.field.targetEndDate')}
                  <input
                    type="date"
                    value={formData.target_end_date}
                    onChange={(e) => handleChange('target_end_date', e.target.value)}
                    className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
                  />
                </label>
              </div>
            </FormRow>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-4 py-3">
          <span className="text-xs text-muted-foreground">{t('task.submitHint')}</span>
          <span className="flex-1" />
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit} isLoading={isSubmitting}>
            {t('project.create')}
            <span className="ml-2 border-l border-primary-foreground/30 pl-2 font-mono text-[10.5px] opacity-75">
              ⌘⏎
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
});

export default ProjectCreatePage;
